import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isUndefined, noop, uniq } from 'lodash/fp';
import { select, scaleBand, scaleLinear, pointer, axisLeft, axisBottom, format } from 'd3';

import styles from './SharedAnalytics.module.scss';
import { Scrollable } from '../scrollable/Scrollable';
import { BarChartAnalytics } from './BarChart';

const { french, white, fontFamily } = styles;

const X_AXIS_LABEL_HEIGHT = 40;

interface VerticalBarChartProps {
    data: BarChartAnalytics[];
    height: number;
    width: number;
    barWidth?: number;
    getBackgroundColor?: (name: string) => string;
    onDblClick?: (id: number) => void;
    getTooltipDetails?: (id: number) => { height: number; width: number; content: string; } | null;
    testId?: string;
    onHover?: (data: BarChartAnalytics | null) => void;
    xAxisLabelHeight?: number;
}

export const VerticalBarChart: React.FC<VerticalBarChartProps> = ({
    data,
    height,
    width,
    barWidth = 50,
    getBackgroundColor = () => french,
    onDblClick = noop,
    getTooltipDetails,
    testId = 'stacked-bar-chart',
    onHover,
    xAxisLabelHeight = X_AXIS_LABEL_HEIGHT
}) => {
    const [maxLabelLines, setMaxLabelLines] = useState(0);
    const svgRef = useRef<SVGSVGElement>(null);
    const svgBarsWidth = useMemo(() => data.length * barWidth, [data.length, barWidth]);
    const svgWidth = useMemo(() => svgBarsWidth > width ? svgBarsWidth : width, [svgBarsWidth, width]);

    const tooltipPosition = useCallback((tooltipWidth: number, tooltipHeight: number, x: number, y: number) => {
        const tooltipOverflowX = x + tooltipWidth > width;
        const tooltipOverflowY = y + tooltipHeight > height;
        const translateX = tooltipOverflowX ? x - (tooltipWidth + 5) : x + 5;
        const translateY = tooltipOverflowY ? y - (tooltipHeight + 5) : y - 5;
        return `translate(${translateX}, ${translateY})`;
    }, [height, width]);

    const upperYScale = useMemo(() => data.reduce((acc, cur) => acc + cur.percentageFill, 0), [data]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrapAxisText = (text: any, width: number): void => {
        let maxLines = 0;
        text.each((_: unknown, i: number, nodes: SVGTextElement[]) => {
            const element = select(nodes[i]);

            const words = element.text().split(/\s+/);
            const lineHeight = 1.1; // em

            // Clear the elements text before we rewrite it with tspan
            element.text(null);

            // add the starting x and y positions for the tspan element. element.attr('x') should anchor this to the middle of the tick
            // as we have text-anchor: middle applied. The dy attribute is set to start just under the tick.
            let tspan = element
                .append('tspan')
                .attr('x', element.attr('x') || 0)
                .attr('dy', '0.75em');

            // Holds the words that belong on the current line
            let line: string[] = [];
            for (const word of words) {
                // Try adding the next word to the current line
                line.push(word);
                tspan.text(line.join(' '));

                // Check if the rendered line is now too wide for the allocated label width
                if (tspan.node()!.getComputedTextLength() > width) {
                    // If it's too wide, remove the word
                    line.pop();
                    tspan.text(line.join(' '));
                    // Now start a brand new line with that word
                    line = [word];
                    // Create a new <tspan> for the new line, placed below the previous one
                    tspan = element.append('tspan')
                        .attr('x', element.attr('x') || 0)
                        .attr('dy', `${lineHeight}em`) // Move downward by line-height (relative shift)
                        .text(word);
                }

            }
            const labelLineCount = element.selectAll('tspan').size();
            maxLines = labelLineCount > maxLines ? labelLineCount : maxLines;
        });
        setMaxLabelLines(maxLines);
    };

    const getYAxisTicks = (max: number, maxTicks = 10): number[] => {
        if (max <= 0) {
            return [0];
        }
        const roundedMax = Math.round(max);
        const increment = roundedMax <= maxTicks ? 1 : Math.ceil(roundedMax / maxTicks);
        const numberOfTicks = roundedMax <= maxTicks ? roundedMax + 1 : maxTicks;
        return uniq([...Array.from({ length: numberOfTicks }, (_, i) => i * increment), roundedMax]);
    };

    useEffect(() => {
        const svg = select(svgRef.current);

        const xScale = scaleBand()
            .domain(data.map(({ id }) => id.toString()))
            .range([2, svgWidth - (barWidth / 2)])
            .padding(0.2);

        const yScale = scaleLinear()
            .domain([0, upperYScale])
            .range([height - xAxisLabelHeight, 5]);

        const yAxis = axisLeft(yScale).tickValues(getYAxisTicks(upperYScale)).tickFormat(d => format('d')(d as number));

        svg.append('g')
            .attr('class', 'y-axis')
            .attr('transform', 'translate(30,0)')
            .call(yAxis)
            .selectAll('text')
            .style('font-family', fontFamily)
            .style('font-size', '12px')
            .style('fill', french);

        const xAxis = axisBottom(xScale).tickFormat((id: string) => data.find(x => x.id.toString() === id)?.name || id).tickSizeOuter(0);

        svg.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(30, ${height - xAxisLabelHeight})`)
            .call(xAxis)
            .selectAll('text')
            .style('font-family', fontFamily)
            .style('font-size', '12px')
            .style('fill', french)
            .attr('text-anchor', 'middle');

        svg.selectAll('.x-axis text').call(wrapAxisText, xScale.bandwidth());

        const g = svg
            .selectAll('.stack-bar-g')
            .data(data)
            .enter()
            .append('g')
            .attr('class', 'stack-bar-g');

        const bars = g.append('rect')
            .attr('id', ({ id }) => `${testId}-filled-${id}`)
            .attr('fill', ({ name }) => getBackgroundColor(name))
            .attr('x', ({ id }) => xScale(id.toString())! + 30)
            .attr('width', xScale.bandwidth())
            .attr('y', yScale(0))
            .attr('height', 0);

        bars.transition()
            .attr('y', ({ percentageFill }) => yScale(percentageFill))
            .attr('height', ({ percentageFill }) => yScale(0) - yScale(percentageFill))
            .duration(1000);

        if (!isUndefined(getTooltipDetails)) {
            g
                .on('mouseover', () => tooltip.attr('display', null))
                .on('mouseout', () => tooltip.attr('display', 'none'))
                .on('mousemove', (e, { id }) => {
                    const [x, y] = pointer(e);
                    const tooltipDetails = getTooltipDetails(id);
                    if (tooltipDetails) {
                        tooltip.attr('transform', tooltipPosition(tooltipDetails.width, tooltipDetails.height, x, y));
                        tooltip.select('rect')
                            .attr('height', tooltipDetails.height)
                            .attr('width', tooltipDetails.width);
                        tooltip.select('text')
                            .html(tooltipDetails.content);
                    }
                });

            const tooltip = svg
                .append('g')
                .attr('class', 'tooltip')
                .attr('data-testid', `analytics-${testId}-tooltip`)
                .attr('display', 'none');

            tooltip.append('rect')
                .attr('rx', 5)
                .attr('stroke', french)
                .attr('stroke-width', 1)
                .attr('fill', white);

            tooltip.append('text')
                .attr('x', 10)
                .attr('y', 4)
                .attr('fill', french)
                .attr('text-anchor', 'start')
                .attr('font-size', '12px')
                .attr('font-family', fontFamily)
                .attr('font-weight', 600);
        }

        setTimeout(() => {
            bars.on('mouseover', (e, d) => !isUndefined(onHover) && onHover(d))
                .on('mouseout', () => !isUndefined(onHover) && onHover(null));
        }, data.length * 250 + 250);

        bars.on('dblclick', (_, { id }) => onDblClick(id));

        return () => {
            svg.selectAll('.stack-bar-g').remove();
            svg.selectAll('.tooltip').remove();
            svg.selectAll('.y-axis').remove();
            svg.selectAll('.x-axis').remove();
        };

    }, [data, width, height, svgWidth, testId, getBackgroundColor, getTooltipDetails, tooltipPosition, onDblClick, onHover, upperYScale, barWidth, xAxisLabelHeight]);

    const labelLineHeight = 13.2; // Accounts for the 12px font and the 1.1em line spacing we apply
    const maxLabelHeight = Math.round(maxLabelLines * labelLineHeight);
    // The X Axis has been shift up by 40px to allow for labels, this provides enough room for two lines of text.
    // However if the text is more than this we need to increase the height of the svg to initiate the scrollable effect.
    // This means we do not need to reduce the height of the chart to allow for the labels in the div as it will now scroll instead.
    const svgHeight = height + (maxLabelHeight - xAxisLabelHeight);

    return (
        <div data-testid={`analytics-${testId}-wrapper`} style={{ height, width }}>
            <Scrollable height={`${height}px`} width={`${width}px`}>
                <svg ref={svgRef} style={{ height: `${svgHeight}px`, width: `${svgWidth}px`, overflow: 'visible' }} />
            </Scrollable>
        </div>
    );
};
